import { Component, computed, effect, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ModalResponseCodes, ModalService } from '@common/components/modal/modal.service';
import { NavigationDragDropData } from '@common/data-models/navigation/navigation-drag-drop-data';
import { ViewStateRecord } from '@common/data-models/records/viewstate-record';
import { NavigationDragDropActions } from '@common/enums/navigation-drag-drop-actions';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { LayoutState } from '@common/services/layout-state';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { Store } from '@ngrx/store';
import { AddViewToNavigationComponent } from '@src/suite/common/components/planning-views/add-view-to-navigation/add-view-to-navigation';
import { DataStore } from '@src/suite/common/data-models/data-store/data-store';
import { NavigationActions, NavigationApiActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { NavigationGroupTypes } from '@src/suite/common/store/navigation/models/navigation.models';
import { selectGroups } from '@src/suite/common/store/navigation/selectors/navigation.selectors';
import { AppService } from '@suite/app.service';
import { firstValueFrom } from 'rxjs';
import { take, tap } from 'rxjs/operators';

@Component({
    selector: 'st-planning-view-item',
    templateUrl: './planning-view-item.html',
    styleUrls: ['./planning-view-item.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-planning-view-item',
    },
})
export class PlanningViewItemComponent extends EnpsComponent implements OnInit, OnDestroy {
    private _record;
    @Input() public record: ViewStateRecord;

    public label: string = '';
    public thumbnail: SafeUrl;
    public loading: boolean = true;
    public success: boolean = false;

    public canDelete: boolean = false;
    public canRemoveFromNavigation: boolean = false;

    public isSelected: boolean = false;
    public isSystem: boolean = false;
    private isTeamView: boolean = false;
    private isOwnView: boolean = false;
    public date: string = '';

    navigation = computed(() => {
        return this.store.selectSignal(selectGroups)();
    });

    constructor(
        public appService: AppService,
        private domSanitizer: DomSanitizer,
        public navigationService: NavigationService,
        public globalTablesService: GlobalTablesService,
        public languageSettings: LanguageSettings,
        public nomWebServicesJson: NomWebServicesJson,
        public viewStateService: LayoutState,
        private modalService: ModalService,
        public dialog: MatDialog,
        private store: Store
    ) {
        super();
        effect(
            () => {
                if (this.navigation()) {
                    this.initPlanningViewFlags();
                }
            },
            { allowSignalWrites: true }
        );
    }

    public get dragData(): NavigationDragDropData {
        const data: NavigationDragDropData = new NavigationDragDropData();
        data.action = NavigationDragDropActions.Add;
        data.data = this.record;
        return data;
    }

    public async deleteNavigationItem(event: Event) {
        event.stopPropagation();

        const modalRef = this.modalService.openModal(this.languageSettings.PSTWebDeleteViewState, this.languageSettings.PSTWebDeletePlanningItemLabel, 'Yes', 'No');
        let modalRes;
        try {
            modalRes = await firstValueFrom(modalRef.result);
        } catch (er) {
            modalRes = ModalResponseCodes.CANCEL;
        }
        if (modalRes === ModalResponseCodes.OK) {
            let groups: Array<number> = [];
            if (this.nomWebServicesJson.HelloResults.Supervisor) {
                groups = [NavigationGroupTypes.Global, NavigationGroupTypes.Team, NavigationGroupTypes.Personal];
            } else {
                groups = [NavigationGroupTypes.Personal];
            }
            this.store.dispatch(
                NavigationActions.removeItemFromGroups({
                    vs: this.record,
                    groups: groups,
                    delete: true,
                })
            );

            DataStore.disposeRecord(this.record);
        }
    }

    public async bookmarkNavigationItem(event: Event) {
        event.stopPropagation();

        if (this.isSelected) {
            // Remove from navigaiton
            if (!this.canRemoveFromNavigation) return;

            let groups: Array<number> = [];
            if (this.isOwnView) {
                groups = [NavigationGroupTypes.Personal];
            } else if (this.nomWebServicesJson.HelloResults.Supervisor) {
                groups = [NavigationGroupTypes.Global, NavigationGroupTypes.Team, NavigationGroupTypes.Personal];
            } else {
                groups = [NavigationGroupTypes.Personal];
            }

            this.store.dispatch(
                NavigationActions.removeItemFromGroups({
                    vs: this.record,
                    groups: groups,
                    delete: false,
                })
            );
        } else {
            // Add to navigaiton
            const dialogRef = this.dialog.open(AddViewToNavigationComponent, {
                hasBackdrop: true,
                delayFocusTrap: true,
                data: {
                    isPersonalView: this.isOwnView,
                    isSupervisor: this.nomWebServicesJson.HelloResults.Supervisor,
                },
            });

            dialogRef
                .afterClosed()
                .pipe(
                    take(1),
                    tap(res => {
                        if (!res) return;
                        this.store.dispatch(NavigationApiActions.addNavigationItem({ group: res.group, section: res.section, vs: this.record }));
                    })
                )
                .subscribe();
        }
    }

    get delete() {
        return this.languageSettings.PSTWebDeletePlanningItemLabel;
    }

    get bookmark() {
        return this.isSelected ? this.languageSettings.PSTWebRemoveFromNavigation : this.languageSettings.PSTWebAddToNavigation;
    }

    public async selectNavigationItem() {
        this.store.dispatch(NavigationActions.loadNavigationItem({ vs: this.record }));
    }

    get createdDate(): string {
        const date = this.record.data.CreateTime.date.toLocaleString().split(',')[0];
        return `${date}`;
    }

    async ngOnInit() {
        if (this.record.viewStateType === ViewStateTypes.SEARCH) {
            this.label = `${this.languageSettings.PSTWebSearchLabel}: ${this.record.title}`;
        } else {
            this.label = this.record.title;
        }
        this.initPlanningViewFlags();

        this.loading = false;
    }

    private initPlanningViewFlags() {
        if (!this.record) return;

        this.isSelected = false;

        let inPersonal: boolean = false;

        for (const group of this.navigation()) {
            if (group.id < 0) continue;
            for (const section of group.items) {
                for (const item of section.items) {
                    if (item && item.viewState && item.viewState.guid === this.record.guid) {
                        this.isSelected = true;
                        if (group.id === NavigationGroupTypes.Personal) {
                            inPersonal = true;
                        }
                    }
                }
            }
        }

        this.canDelete = false;
        this.canRemoveFromNavigation = false;

        this.isOwnView = this.record.path.isOwnView;

        if (this.nomWebServicesJson.HelloResults.Supervisor) {
            this.canDelete = true;
            this.canRemoveFromNavigation = true;
        } else {
            if (this.isOwnView) {
                this.canDelete = true;
                this.canRemoveFromNavigation = true;
            } else {
                this.canDelete = false;
                if (inPersonal) {
                    this.canRemoveFromNavigation = true;
                } else {
                    this.canRemoveFromNavigation = false;
                }
            }
        }
    }
}
